import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { IAPIArrayData, IAPIData, APIuuid } from '@shared/interfaces/api.interface';
import { IJob } from '@shared/interfaces/job.interface';
import { IPaginable } from '@shared/interfaces/paginable.interface';
import { Job } from '@shared/models/job.model';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ITypeahead } from '@shared/interfaces/typeahead.interface';
import { ITypeaheadable } from '@shared/interfaces/typeaheadable.interface';
import { Typeahead } from '@shared/models/typeahead.model';

@Injectable({
  providedIn: 'root'
})
export class JobsService implements IPaginable, ITypeaheadable {
  private readonly ENDPOINT_V3: string = String(
    `${environment.ENDPOINTS.BACK_END.V3}/jobs`
  );

  constructor(private readonly _http: HttpClient) {}

  public get(params: any = {}): Observable<IJob[]> {
    let httpParams: HttpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach(key => {
        httpParams = httpParams.set(key, params[key]);
      });
    }

    httpParams = httpParams.set('include', 'question_set,hiring_firm');

    return this._http
      .get<IAPIArrayData>(this.ENDPOINT_V3, {
        params: httpParams
      })
      .pipe(map((res: IAPIArrayData) => Job.fromAPIArray(res)));
  }

  public find(id: APIuuid): Observable<IJob> {
    let params: HttpParams = new HttpParams();
    params = params.set('include', 'question_set,hiring_firm');

    return this._http
      .get<IAPIData>(`${this.ENDPOINT_V3}/${id}`, { params })
      .pipe(map((res: IAPIData) => Job.fromAPI(res)));
  }

  public delete(id: APIuuid): Observable<IJob> {
    return this._http
      .delete<IAPIData>(`${this.ENDPOINT_V3}/${id}`)
      .pipe(map((res: IAPIData) => Job.fromAPI(res)));
  }

  public getPage(
    page: number,
    onGetRawData?: (data: IAPIArrayData) => void
  ): Observable<IJob[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('page[number]', String(page));
    params = params.set('include', 'question_set,hiring_firm');

    return this._http.get<IAPIArrayData>(this.ENDPOINT_V3, { params }).pipe(
      tap((data: IAPIArrayData) => onGetRawData && onGetRawData(data)),
      map((res: IAPIArrayData) => Job.fromAPIArray(res))
    );
  }

  public patch(body: IAPIData): Observable<IJob> {
    const id = body.data.id;
    let params: HttpParams = new HttpParams();
    params = params.set('include', 'question_set,hiring_firm');

    return this._http
      .patch<IAPIData>(`${this.ENDPOINT_V3}/${id}`, body, { params })
      .pipe(map((res: IAPIData) => Job.fromAPI(res)));
  }

  public post(body: IAPIData): Observable<IJob> {
    let params: HttpParams = new HttpParams();
    params = params.set('include', 'question_set,hiring_firm');

    return this._http
      .post<IAPIData>(this.ENDPOINT_V3, body, { params })
      .pipe(map((res: IAPIData) => Job.fromAPI(res)));
  }

  public searchTypeahead(search: {
    data: { search_criteria: string };
  }): Observable<ITypeahead> {
    let params: HttpParams = new HttpParams();
    params = params.set('include', 'question_set,hiring_firm');

    return this._http
      .post<IAPIData>(`${this.ENDPOINT_V3}/search`, search, { params })
      .pipe(map((res: IAPIData) => Typeahead.fromAPI(res)));
  }
}
